import {
  Component,
  Input,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NbColorHelper, NbDialogService, NbThemeService } from '@nebular/theme';
import { finalize, takeUntil } from 'rxjs/operators';
import { NbToastrService } from '@nebular/theme';
import { Subject } from 'rxjs/Subject';

import { Observable } from 'rxjs';
import {
  Goal,
  GoalData,
  GoalView,
} from '../../../../@core/interfaces/common/CalorieFriend/goals';

import { GoalDefComponent } from '../../Goals/GoalDef/goal-def.component';

import { Chart } from 'chart.js';
import * as ChartAnnotation from 'chartjs-plugin-annotation';
import {
  Patient,
  PatientData,
} from 'app/@core/interfaces/common/CalorieFriend/patients';
import { ChangeDetectorRef } from '@angular/core';
import { ConfirmMessageDialogComponent } from 'app/@components/confirm-message-dialog/confirm-message-dialog.component';
import { GridData } from 'app/@core/interfaces/common/grid-data';

/**
 * Component for listing the Goals items of the Patient
 * as Graphs and Tables on the Patient Sheet View page
 */
@Component({
  selector: 'app-goals-list-view',
  templateUrl: './goals-list-view.component.html',
  styleUrls: [
    './goals-list-view.component.scss',
    '../../global.component.scss',
  ],
})
export class GoalsListViewComponent implements OnInit {
  GoalListLabel = 'Goals';
  AddGoalLabel = 'Add New Goal';
  EditGoalLabel = 'Edit';
  RemoveGoalLabel = 'Remove';
  GoalNameLabel = 'Name';
  GoalActiveDaysLabel = 'Days';
  GoalActiveStateLabel = 'Active';
  AcceptLabel = 'SAVE PLANS';
  BackLabel = 'Back';
  TemplateLabel = 'Template';
  SucessDeleteGoal = 'Sucessfully deleted Goal';
  FailDeleteGoal = 'Problem occurred deelting Goal';

  @ViewChildren('chart') charts: QueryList<any>;

  @Input('PatientID') PatientID: number = null;
  @Input('AllowEdit') AllowEdit: boolean = true;
  @Input('NotificationToken') NotificationToken: string = null;

  @Input('IsView') IsView: boolean = false;

  Goals: Array<GoalView> = null;
  GraphData: Array<any> = null;
  GoalListSource: Observable<GridData<GoalView>> = null;
  isLoading = false;

  Patient: Patient = null;

  protected readonly unsubscribe$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private dialogService: NbDialogService,
    private goalsService: GoalData,
    private patientService: PatientData,
    private toasterService: NbToastrService,
    private theme: NbThemeService,
    protected cd: ChangeDetectorRef,
  ) {
    const namedChartAnnotation = ChartAnnotation;
    namedChartAnnotation['id'] = 'annotation';
    Chart.pluginService.register(namedChartAnnotation);
  }

  /**
   * Initializes the component.
   * It gets the Patient ID from the URL and initializes the settings for the Goals list of that Patient.
   */
  ngOnInit(): void {
    if (this.PatientID == null || this.PatientID === undefined)
      this.PatientID = +this.route.snapshot.paramMap.get('PatientId');

    if (this.NotificationToken == null || this.NotificationToken === undefined)
      this.NotificationToken =
        this.route.snapshot.paramMap.get('NotificationToken');

    this.GoalListSource = this.goalsService.GoalsList(
      this.PatientID,
      this.NotificationToken,
    );

    this.loadPatientData();

    this.refreshGoals();

    // binding change message of the service
    this.patientService.onUpdated$.subscribe((id) => {
      if (this.PatientID === id) {
        this.loadPatientData();
      }
    });
  }

  /**
   * Loads the data of the Patient given by {PatientID}
   * @param id The identifier of the Goal
   */
  loadPatientData() {
    if (this.PatientID || this.NotificationToken) {
      this.patientService
        .get(this.PatientID, this.NotificationToken)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe((p) => {
          this.Patient = p;
        });
    }
  }

  /**
   * Function to get the name from the Goal
   * @param goal
   */
  getGoalName(goal) {
    return (
      goal.goalName +
      ' (' +
      (goal.status === 0
        ? $localize`:@@Active:Active`
        : goal.status === 1
        ? $localize`:@@Achieved:Achieved`
        : $localize`:@@Expired:Expired`) +
      ')'
    );
  }

  /**
   * Method to refresh the goals list with data, progress bar and graph.
   */
  public refreshGoals() {
    this.isLoading = true;
    this.GoalListSource
    .pipe(finalize(() => this.isLoading = false))
    .subscribe((res) => {
      this.Goals = res.items;

      this.themeSubscription = this.theme.getJsTheme().subscribe((config) => {
        const colors = config.variables;
        const chartjs: any = config.variables.chartjs;

        this.GraphData = new Array();

        for (const g of this.Goals) {
          const labels = g.biometricsData.map((b) => b.date.toString());
          let values = g.biometricsData
            .filter((b) => b.value >= 0)
            .map((b) => b.value);
          let planValues = g.biometricsData.map((b) => b.planValue);

          if (this.Patient && this.Patient.isPreferredUnitImperial) {
            if (g.goalType === 'Weight') {
              g.goalValue = Math.round((g.goalValue * 2.20462));
              g.startValue = Math.round((g.startValue * 2.20462));
              values = values.map((b) => Math.round((b * 2.20462)));
              planValues = planValues.map(
                (v) => Math.round(v * 2.20462),
              );
            }
          } else {
            g.goalValue = Math.round(g.goalValue);
            g.startValue = Math.round(g.startValue);
          }

          const data = {
            labels: labels,
            datasets: [
              {
                data: values,
                label: $localize`:@@Measure:Measure`,
                fill: false,
                backgroundColor: '#3498db',
                borderColor: '#3498db',
                borderWidth: 7
              },
              {
                data: planValues,
                label: $localize`:@@Plan:Plan`,
                fill: false,
                backgroundColor: '#2ecc71',
                borderColor: '#2ecc71',
                borderWidth: 5
              },
            ],
          };

          this.GraphData.push(data);
        }

        this.options = {
          responsive: true,
          maintainAspectRatio: false,
          animation: {
            duration: 0,
          },
          scales: {
            xAxes: [
              {
                gridLines: {
                  display: false,
                },
                ticks: {
                  fontColor: chartjs.textColor,
                },
              },
            ],
            yAxes: [
              {
                gridLines: {
                  display: false,
                },
                ticks: {
                  fontColor: chartjs.textColor,
                },
              },
            ],
          },
          legend: {
            labels: {
              fontColor: chartjs.textColor,
              usePointStyle : true,
              pointStyle: 'circle',
              lineWidth: 0,
            },
            position: 'bottom',
          },
          elements: {
            point:{
              radius: 0
            }
          },
          annotation: {
            annotations: [
              {
                type: 'line',
                mode: 'vertical',
                scaleID: 'x-axis-0',
                value: this.getFormattedTodayDate(),
                borderColor: 'orange',
                borderWidth: 2,
                labelOptions: {
                  overflow: 'none',
                  shape: 'rect',
                },
                label: {
                  enabled: true,
                  fontColor: 'orange',
                  content: 'Today',
                  position: 'bottom',
                  xAdjust: -20,
                },
              },
            ],
          },
        };
      });

      this.forceGoalsChartRefresh();
    });
  }

  forceGoalsChartRefresh() {
    setTimeout(() => {
      for (const chart of this.charts) {
        chart.chart.canvas.style =
          'display: block; height: 250px; width: 100%;';
        chart.chart.update();
      }
      this.cd.detectChanges();
    }, 30);
  }

  getFormattedTodayDate(): string {
    const date = new Date();

    let month = (1 + date.getMonth()).toString();
    month = month.length > 1 ? month : '0' + month;

    let day = date.getDate().toString();
    day = day.length > 1 ? day : '0' + day;

    return month + '/' + day;
  }

  /**
   * Method to add a new goal.
   * It opens the 'GoalDefComponent' component as a pop up dialog.
   */
  AddGoal() {
    this.dialogService
      .open(GoalDefComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: false,
        context: { PatientID: this.PatientID },
      })
      .onClose.subscribe((goal) => this.refreshGoals());
  }

  UpdateGoal(goal: Goal) {
    if (goal !== null && goal !== undefined) {
      // this.Goals = this.Goals.filter(goal => goal.id !== Goal.id || goal.name !== Goal.name);
      // this.Goals.push(Goal);
      // this.GoalSource = new LocalDataSource(this.Goals); // create the source
    }
  }

  /**
   * Method to edit an existing goal.
   * It opens the 'GoalDefComponent' component as a pop up dialog.
   */
  onEdit(row) {
    // this.router.navigate([`/pages/CalorieFriend/Goal/${this.PatientID}/${this.Goals[row].id}`]);

    this.dialogService
      .open(GoalDefComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: false,
        context: {
          PatientID: this.PatientID,
          ID: this.Goals[row].id,
        },
      })
      .onClose.subscribe((goal) => this.refreshGoals());
  }

  /**
   * Event handler of the 'Click' event of the 'Delete' action.
   * It deletes a Goal in the row of index number - {row}.
   * @param row Row index number
   */
  onDelete(row) {
    this.dialogService
      .open(ConfirmMessageDialogComponent, {
        closeOnEsc: true,
        closeOnBackdropClick: true,
        context: { Message: 'Are you sure to delete this goal?' },
      })
      .onClose.subscribe((result) => {
        if (result) {
          const observable = this.goalsService.delete(this.Goals[row].id);
          observable.pipe(takeUntil(this.unsubscribe$)).subscribe(
            () => {
              this.handleGoalDeleteSuccessResponse();
              this.refreshGoals();
            },
            (err) => {
              this.handleGoalDeleteWrongResponse();
            },
          );
        }
      });
  }

  /**
   * Handler of Success event of Delete action of an existing goal
   */
  handleGoalDeleteSuccessResponse() {
    this.toasterService.success('', this.SucessDeleteGoal);
  }

  /**
   * Handler of Fail event of Delete action of an existing goal
   */
  handleGoalDeleteWrongResponse() {
    this.toasterService.danger('', this.FailDeleteGoal);
  }

  Accept() {}

  Back() {}

  getGoalDate(dateString): string {
    const date = new Date(dateString);
    return date.toDateString();
  }

  //#region Graph

  data: any;
  options: any;
  themeSubscription: any;

  //#endregion
}
